import type { LogEntry } from "../core/eventLog";
import { extractContactId, type ContactSummary } from "../core/contactId";

/** Contact events that mean the roster and attribute snapshot must refresh. */
const CONTACT_CAPTURE_EVENTS = new Set([
  "onIncoming",
  "onConnecting",
  "onConnected",
  "onPending",
  "onMissed",
  "onStartingAcw",
  "onCleared",
  "onDestroyed",
  "onError",
  "onParticipantAdded",
  "onParticipantDisconnected",
  "onParticipantStateChanged",
]);

/** True when this entry is a contact lifecycle/participant event we should snapshot. */
export function shouldCaptureContact(entry: LogEntry): boolean {
  return entry.moduleId === "contact" && CONTACT_CAPTURE_EVENTS.has(entry.name);
}

/** Upserts a contact into the roster from an event payload, preserving prior rows. */
export function upsertContact(
  existing: ContactSummary[],
  entry: LogEntry,
): ContactSummary[] {
  const contactId = entry.contactId ?? extractContactId(entry.payload);
  if (!contactId) return existing;
  const payload = isRecord(entry.payload) ? entry.payload : {};
  const next: ContactSummary = {
    contactId,
    type: str(payload.type) ?? existing.find((c) => c.contactId === contactId)?.type,
    subtype:
      str(payload.subtype) ??
      existing.find((c) => c.contactId === contactId)?.subtype,
    initialContactId:
      str(payload.initialContactId) ??
      existing.find((c) => c.contactId === contactId)?.initialContactId,
  };
  const rest = existing.filter((c) => c.contactId !== contactId);
  return [next, ...rest];
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

const str = (v: unknown): string | undefined =>
  typeof v === "string" && v.length > 0 ? v : undefined;

/** Merges probe results with contacts already discovered from live events. */
export function mergeContacts(
  existing: ContactSummary[],
  incoming: ContactSummary[],
): ContactSummary[] {
  const byId = new Map(existing.map((c) => [c.contactId, c]));
  for (const c of incoming) {
    const prev = byId.get(c.contactId);
    byId.set(c.contactId, prev ? { ...prev, ...c } : c);
  }
  return [...byId.values()];
}

type CaptureManagers = {
  tracker: { capture: (contactId: string) => Promise<unknown> };
  runner: {
    invoke: (
      moduleId: string,
      method: string,
      values: Record<string, unknown>,
    ) => Promise<unknown>;
  };
};

/**
 * Applies one newly-appended log entry to the roster and, for contact lifecycle
 * events, snapshots attributes + participants (monitor/barge shows up here).
 */
export function onLiveEntry(
  entry: LogEntry,
  managers: CaptureManagers | null,
  setContacts: (fn: (prev: ContactSummary[]) => ContactSummary[]) => void,
  setSelectedContactId: (fn: (prev: string | undefined) => string | undefined) => void,
): void {
  const contactId = entry.contactId ?? extractContactId(entry.payload);
  if (entry.moduleId === "contact" && contactId) {
    setContacts((prev) => upsertContact(prev, { ...entry, contactId }));
    setSelectedContactId((prev) => prev ?? contactId);
  }
  if (!managers || !shouldCaptureContact(entry) || !contactId) return;
  void managers.tracker.capture(contactId);
  void managers.runner.invoke("contact", "listParticipants", { contactId });
}
